/**
 * Filtering the requirements board (point 20).
 *
 * ---- Client-side, and why that is not a shortcut ----
 *
 * `RequirementsPage` already fetches the whole board in one page (`limit: 200`, scoped to the
 * viewer's role server-side), because a Kanban has to count every column to draw itself. Filtering
 * those rows here costs no request and keeps one invariant that a server-side filter would break:
 * the column counts and the cards under them come from the SAME list. Ask the server for a subset
 * and the counts describe a different population than the cards do.
 *
 * The trade is honest and bounded: this narrows the page the board holds, not the whole table. The
 * page already says "Showing N of M" when the two differ, so a purchaser filtering a truncated
 * board can see that is what happened.
 *
 * Nothing here reads a display label as a key — `customer` is the Customer docname, never
 * `customer_name`. Two customers sharing a name is the single most-repeated bug in this codebase
 * (CLAUDE.md's API conventions); the name is what we SHOW in the picker, the id is what we match.
 */

import { ENQUIRY_STATUSES } from './types'
import type { EnquiryStatus, EnquirySummary } from './types'

export interface RequirementFilters {
  /** Free text over id / ref / customer name / quotation number / vessel — what people have to hand. */
  search: string
  /** Customer DOCNAME. */
  customer: string
  enquiry_type: string
  purchaser: string
  /**
   * Pipeline stages to keep. A LIST, not a single value, because the inbox strip's "to compare"
   * tile covers two stages at once (`inboxCounts.ts`) and its link has to open the board on the
   * same population it counted. Empty means every status.
   */
  status: EnquiryStatus[]
  /**
   * Inclusive `YYYY-MM-DD` bounds on the enquiry's own date. Either end may stand alone ("since
   * the 1st", "up to the 15th"). Compared as STRINGS — see `applyRequirementFilters`.
   */
  date_from: string
  date_to: string
}

export const EMPTY_FILTERS: RequirementFilters = {
  search: '',
  customer: '',
  enquiry_type: '',
  purchaser: '',
  status: [],
  date_from: '',
  date_to: '',
}

const KNOWN_STATUSES = new Set<string>(ENQUIRY_STATUSES)

/**
 * `?status=RFQ Sent,Quotes Received` → the statuses to filter by. Built by the inbox strip
 * (point 29); also perfectly typeable by hand, which is why unknown values are dropped rather
 * than kept: a status this client does not know matches no row, and silently keeping it would
 * leave an empty board with an invisible cause.
 */
export function statusFilterFromQuery(raw: string | null | undefined): EnquiryStatus[] {
  return (raw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is EnquiryStatus => s !== '' && KNOWN_STATUSES.has(s))
}

const norm = (v: unknown) => String(v ?? '').trim().toLowerCase()

export function applyRequirementFilters(
  rows: readonly EnquirySummary[],
  filters: RequirementFilters,
): EnquirySummary[] {
  const search = norm(filters.search)
  return rows.filter((r) => {
    if (filters.customer !== '' && r.customer !== filters.customer) return false
    if (filters.enquiry_type !== '' && r.enquiry_type !== filters.enquiry_type) return false
    if (filters.purchaser !== '' && r.purchaser !== filters.purchaser) return false
    if (filters.status.length > 0 && !filters.status.includes(r.status)) return false

    /*
      String comparison, deliberately: `YYYY-MM-DD` already sorts chronologically, and the wire's
      dates are site-local with no zone on them (CLAUDE.md's API conventions). Parsing them into a
      `Date` would attach the BROWSER's zone to a value that never had one, and an enquiry dated
      the 1st would fall outside a range starting on the 1st for anyone west of the site.

      A row with no date is excluded once a range is set: it cannot be shown to fall inside one,
      and including it would let "August only" quietly carry undated work.
    */
    if (filters.date_from !== '' || filters.date_to !== '') {
      const date = r.date ?? ''
      if (date === '') return false
      if (filters.date_from !== '' && date < filters.date_from) return false
      if (filters.date_to !== '' && date > filters.date_to) return false
    }
    if (search === '') return true
    // `ref` is what the customer quotes back at us and `id` is what we quote internally; both have
    // to match, and so does the customer name, which is what gets typed most. The quotation
    // number and the vessel too (2026-09-15): the number on the document the customer answers,
    // and the name the requirement is talked about by.
    return (
      norm(r.id).includes(search) ||
      norm(r.ref).includes(search) ||
      norm(r.customer_name).includes(search) ||
      norm(r.quotation_id).includes(search) ||
      norm(r.vessel).includes(search)
    )
  })
}

/**
 * The values the pickers offer — derived from the rows on screen, never a fixed enum list.
 *
 * A dropdown offering `Bonded Stores` when no bonded-stores enquiry is loaded gives the purchaser
 * a choice whose only outcome is an empty board, and reads as a broken filter rather than an empty
 * category. Every option here is guaranteed to return at least one card.
 */
export function filterOptions(rows: readonly EnquirySummary[]): {
  customers: { value: string; label: string }[]
  enquiryTypes: string[]
  purchasers: string[]
} {
  const customers = new Map<string, string>()
  const enquiryTypes = new Set<string>()
  const purchasers = new Set<string>()

  for (const r of rows) {
    // Keyed on the docname; the name is display only. First non-empty name wins — later rows for
    // the same customer carry the same one.
    if (r.customer) customers.set(r.customer, customers.get(r.customer) || r.customer_name || r.customer)
    if (r.enquiry_type) enquiryTypes.add(r.enquiry_type)
    if (r.purchaser) purchasers.add(r.purchaser)
  }

  return {
    customers: [...customers.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    enquiryTypes: [...enquiryTypes].sort(),
    purchasers: [...purchasers].sort(),
  }
}

/** How many filters are actually narrowing anything — drives the "Clear" control and its badge. */
export function activeFilterCount(filters: RequirementFilters): number {
  const text = [filters.search.trim(), filters.customer, filters.enquiry_type, filters.purchaser].filter(
    (v) => v !== '',
  ).length
  // A range is ONE filter however many ends are set — "from the 1st to the 31st" is a single idea,
  // and counting it twice would make the Clear badge read 2 for one choice.
  const range = filters.date_from !== '' || filters.date_to !== '' ? 1 : 0
  return text + (filters.status.length > 0 ? 1 : 0) + range
}
